import React, { useEffect } from 'react';
import HeroSlider from '../components/HeroSlider';
import ScrollReveal from '../components/ScrollReveal';
import { Link } from 'react-router-dom';

const Home = () => {
  useEffect(() => {
    const handleScrollReveal = () => {
      const reveals = document.querySelectorAll('.scroll-reveal');
      reveals.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) el.classList.add('visible');
      });
    };
    window.addEventListener('scroll', handleScrollReveal);
    handleScrollReveal();
    return () => window.removeEventListener('scroll', handleScrollReveal);
  }, []);

  const industries = [
    { title: "Technology & Consulting", desc: "Innovative solutions and strategic consulting services that drive digital transformation and business agility.", icon: "💻", link: "/industries/tech-cons" },
    { title: "Infrastructure & Development", desc: "Building the future with world-class infrastructure, sustainable engineering and smart city solutions.", icon: "🏗️", link: "/industries/builders-dev" },
    { title: "Media & Entertainment", desc: "Creating captivating content across multiple platforms, from film production to digital storytelling.", icon: "🎬", link: "/industries/media-entertainment" },
    { title: "Luxury & Retail", desc: "Premium jewelry and retail experiences that blend timeless craftsmanship with modern elegance.", icon: "💎", link: "/industries/luxury-retail" },
    { title: "Social Impact", desc: "Making a lasting difference through charitable initiatives, community empowerment and sustainable development.", icon: "🤝", link: "/industries/social-impact" }
  ];
  const values = [
    { title: "Excellence", desc: "Commitment to delivering the highest quality in everything we do", icon: "🏆" },
    { title: "Integrity", desc: "Building trust through transparency and ethical business practices", icon: "⚖️" },
    { title: "Innovation", desc: "Embracing change and driving transformation across industries", icon: "💡" },
    { title: "Social Responsibility", desc: "Creating positive impact in communities we serve", icon: "🌍" }
  ];

  /**
   * Visible FAQ — these answers are also emitted as FAQPage JSON-LD from
   * seoConfig.js. Google requires FAQ rich-result content to be visible on the
   * page, so the two must stay in sync. Edit both together.
   */
  const faqs = [
    {
      q: "Which are the top business companies in Guntur?",
      a: "AALI GROUP is one of the leading diversified business groups in Guntur, Andhra Pradesh. It operates eight companies across software and digital marketing (AALITECH), job placement and education consultancy (AALI CONSSULTANCY), construction and real estate (AALI BUILDERS), film and media production (AALI MOVIES, AALI FILMZ), music (AALI MUSIC), gold jewellery retail (AALI GOLD JEWELZ) and charitable social work (AALI TRUSTZ)."
    },
    {
      q: "Where is AALI GROUP located in Guntur?",
      a: "The AALI GROUP head office is at AALITECH, 4th Line, Syamala Nagar, Guntur, Andhra Pradesh 522006, India. Office hours are Monday to Saturday, 9:00 AM to 7:00 PM. You can call +91 83410 85415 or email info@aaligroup.co."
    },
    {
      q: "What businesses does AALI GROUP operate in Andhra Pradesh?",
      a: "AALI GROUP operates across five industries in Andhra Pradesh: technology and job consultancy, infrastructure and real estate development, media and entertainment, luxury and gold jewellery retail, and social impact through its charitable trust. The group serves Guntur, Vijayawada, Amaravati, Tenali, Mangalagiri,Narasaraopet and more."
    },
    {
      q: "Which AALI GROUP company offers software and IT services?",
      a: "AALITECH, the technology company of AALI GROUP, builds websites, mobile apps and business software and runs digital marketing (SEO, social media and PPC) for businesses in Guntur and across Andhra Pradesh. Its official website is aalitech.co."
    },
    {
      q: "Who is the founder of AALI GROUP?",
      a: "AALI GROUP was founded by Azagar Ali Shaik, who serves as Founder and Chairman. The group was established in 2021 and is headquartered in Guntur, Andhra Pradesh."
    },
    {
      q: "How can I contact AALI GROUP for business enquiries?",
      a: "You can reach AALI GROUP by phone at +91 83410 85415, by email at info@aaligroup.co, or through the contact form on this website. The head office in Syamala Nagar, Guntur is open Monday to Saturday."
    }
  ];

  return (
    <>
      <HeroSlider />

      {/* ── Intro band: carries the page's only <h1> and the local keywords.
             Google cannot rank a page for "companies in Guntur" if the words
             never appear on it. Split into a text column + stat cards so it
             reads as a designed section rather than a block of copy. ── */}
      <section className="intro-band">
        <div className="container">
          <div className="intro-grid">

            <div className="intro-lead">
              <span className="intro-eyebrow">AALI GROUP · Since 2021</span>
              <h1 className="intro-title">
                One of the <span>Top Business Companies</span> in Guntur, Andhra Pradesh
              </h1>
              <p className="intro-text">
                A diversified business group headquartered in Syamala Nagar, Guntur — eight
                companies spanning software &amp; IT services, business consulting, construction
                &amp; real estate, film, media &amp; music production, gold jewellery retail and
                charitable social work. We serve clients across Guntur, Vijayawada, Amaravati,
                Tenali, Mangalagiri, Narasaraopet and more.
              </p>
              <div className="intro-actions">
                <Link to="/businesses" className="intro-btn">Explore Our Companies →</Link>
                <a href="tel:+918341085415" className="intro-btn-ghost">
                  <i className="fas fa-mobile-screen-button" aria-hidden="true" /> +91 83410 85415
                </a>
              </div>
            </div>

            <div className="intro-stats">
              {[
                { num: '8',    label: 'Companies' },
                { num: '5',    label: 'Industries' },
                { num: '2021', label: 'Founded' },
                { num: '6+',   label: 'Cities Served' },
              ].map((stat, idx) => (
                <div className="intro-stat" key={idx}>
                  <strong>{stat.num}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>
      <section className="founder-section">
        <div className="founder-container">
          <div className="founder-image">
            <img src="/assets/opt/ceo.webp" alt="Founder - Azagar Ali Shaik" />
          </div>
          <div className="founder-content">
            <div className="glass-card">
              <div className="founder-quote-box">
                <p className="founder-quote">“ Excellence is not defined by what we achieve alone, but by how we uplift others along the journey. At AALI GROUP, we harness the power of innovation, integrity, and collective ambition to create a legacy that transcends generations. We don't just build businesses — we architect futures with purpose. ”</p>
                <div className="quote-arrow"></div>
              </div>
              <div className="founder-meta">
                <div className="founder-details">
                  <h4>Azagar Ali Shaik</h4>
                  <span>Founder & Chairman, AALI GROUP</span>
                </div>
                <Link to="/founder" className="founder-btn">View Profile →</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="industries">
        <div className="container">
          <h2 className="section-title">Our Industries</h2>
          <p className="section-subtitle">Diversified excellence across five dynamic sectors</p>
          <div className="industries-grid">
            {industries.map((ind, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="industry-card">
                  <div className="industry-icon" style={{ background: '#FFF0E5', fontSize: '40px' }}>{ind.icon}</div>
                  <h3 className="industry-title">{ind.title}</h3>
                  <p className="industry-description">{ind.desc}</p>
                  <Link to={ind.link} className="industry-link">Explore</Link>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      <section className="core-values">
        <div className="container">
          <h2 className="section-title">Our Core Values</h2>
          <p className="section-subtitle">Principles that drive our success and shape our future</p>
          <div className="values-grid">
            {values.map((val, idx) => (
              <ScrollReveal key={idx} delay={idx * 100}>
                <div className="value-card">
                  <div className="value-icon" style={{ fontSize: '48px' }}>{val.icon}</div>
                  <h3 className="value-title">{val.title}</h3>
                  <p className="value-description">{val.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      
      {/* ── FAQ – mirrors the FAQPage JSON-LD in seoConfig.js.
             Targets the long-tail questions people actually type into Google.
             Keep the visible answers and the JSON-LD in sync: Google only
             grants FAQ rich results when the answers are on the page. ── */}
      <section className="faq-section">
        <div className="container">
          <div className="faq-grid">

            <div className="faq-intro">
              <span className="intro-eyebrow">FAQ</span>
              <h2 className="faq-heading">Questions we get asked</h2>
              <p className="faq-subtext">
                Everything you might want to know about AALI GROUP and our companies
                in Guntur, Andhra Pradesh.
              </p>
              <Link to="/contact" className="intro-btn">Talk to Us →</Link>
            </div>

            <div className="faq-list">
              {faqs.map((item, idx) => (
                <ScrollReveal key={idx} delay={idx * 60}>
                  <details className="faq-item">
                    <summary className="faq-question">{item.q}</summary>
                    <p className="faq-answer">{item.a}</p>
                  </details>
                </ScrollReveal>
              ))}
            </div>

          </div>
        </div>
      </section>

      <div className="cta-section">
        <div className="container">
          <h2 className="cta-title">Join Our Journey of Excellence</h2>
          <p className="cta-description">Explore career opportunities across our diverse portfolio of companies</p>
          <Link to="/careers" className="btn btn-white">Explore Careers →</Link>
        </div>
      </div>
    </>
  );
};
export default Home;